import * as React from 'react'
import { useEffect, useState, useRef } from 'react'
import { Form, Input, Button, Row, Col } from 'antd-v4'
import { OrganizationsAPI, OrganizationsModels } from '@getgreenline/organizations'
import { CompaniesApi, CompaniesModels } from '@getgreenline/companies'
import { FormProps } from 'antd-v4/lib/form'
import { Icon, message } from 'antd'
import { parseErrorMsg } from '../../../utilities/helpers'
import {
  getOrganizationWithCompanies,
  IOrganizationWithCompanies,
  removeCompanyFromOrganization,
} from './organizationsApi'

interface Props<T extends OrganizationsModels.ICreateOrganization> extends FormProps<T> {
  existingOrganization?: OrganizationsModels.IOrganization
  shouldResetFields: boolean
  onSubmit: (externalSourceObject: T) => void
  // Rendered below the companies block in edit mode (e.g. the user migration section)
  children?: React.ReactNode
}

type TOrgForm = <T extends OrganizationsModels.ICreateOrganization>(
  props: Props<T>,
) => React.ReactElement

const CompanyCard = ({
  name,
  id,
  province,
  onRemove,
}: {
  name: string
  id: number
  province: string | null
  onRemove: () => void
}) => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '8px 12px',
      border: '1px solid #f0f0f0',
      borderRadius: 4,
      marginBottom: 8,
      background: '#fafafa',
    }}
  >
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <Icon type='shop' style={{ color: 'rgba(0,0,0,.45)' }} />
      <div>
        <div style={{ fontWeight: 500 }}>{name}</div>
        <div style={{ fontSize: 12, color: 'rgba(0,0,0,.45)' }}>ID: {id}</div>
      </div>
    </div>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      {province && (
        <span
          style={{
            fontSize: 12,
            padding: '1px 6px',
            borderRadius: 3,
            background: '#e6f7ff',
            color: '#096dd9',
            border: '1px solid #91d5ff',
          }}
        >
          {province}
        </span>
      )}
      <Button
        type='link'
        size='small'
        onClick={onRemove}
        style={{ color: 'rgba(0,0,0,.45)', padding: 0 }}
      >
        <Icon type='close' />
      </Button>
    </div>
  </div>
)

export const OrganizationForm: TOrgForm = <T extends OrganizationsModels.ICreateOrganization>(
  props: Props<T>,
) => {
  const { onSubmit, existingOrganization, shouldResetFields } = props

  const [form] = Form.useForm()

  const validateMessages = {
    required: '${label} is required!',
  }

  // Edit mode state
  const [assignedCompanies, setAssignedCompanies] = useState<
    IOrganizationWithCompanies['companies']
  >([])
  const [pendingAdditions, setPendingAdditions] = useState<CompaniesModels.ICompany[]>([])
  const [pendingRemovals, setPendingRemovals] = useState<number[]>([])
  const [searchResults, setSearchResults] = useState<CompaniesModels.ICompany[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [searchOpen, setSearchOpen] = useState(false)
  const [isSearching, setIsSearching] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [nameChanged, setNameChanged] = useState(false)
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isMountedRef = useRef(true)
  const searchContainerRef = useRef<HTMLDivElement>(null)
  const [dropdownRect, setDropdownRect] =
    useState<{ top: number; left: number; width: number } | null>(null)

  useEffect(() => {
    return () => {
      isMountedRef.current = false
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current)
    }
  }, [])

  const isEditMode = !!existingOrganization

  useEffect(() => {
    if (!existingOrganization) return
    let cancelled = false
    ;(async () => {
      try {
        const org = await getOrganizationWithCompanies(existingOrganization.id)
        if (!cancelled) setAssignedCompanies(org.companies || [])
      } catch (error) {
        if (!cancelled)
          message.error(parseErrorMsg(error) || 'Failed to load organization companies')
      }
    })()
    return () => {
      cancelled = true
    }
  }, [existingOrganization?.id])

  const hasPendingChanges = pendingAdditions.length > 0 || pendingRemovals.length > 0 || nameChanged

  const handleCompanySearch = (value: string) => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current)
    if (!value) {
      setSearchResults([])
      return
    }
    searchDebounceRef.current = setTimeout(async () => {
      if (!isMountedRef.current) return
      setIsSearching(true)
      try {
        const isNumeric = /^\d+$/.test(value.trim())
        const payload = isNumeric
          ? { id: value.trim(), limit: 1 }
          : { name: value.trim(), limit: 20 }
        const { data } = await CompaniesApi.getCursorPaginatedCompanies(payload)

        // Filter out companies already assigned or pending addition
        const assignedIds = new Set([
          ...assignedCompanies.map((c) => c.id),
          ...pendingAdditions.map((c) => c.id),
        ])
        if (isMountedRef.current)
          setSearchResults(data.filter((c) => !assignedIds.has(c.id) && !c.parentEntityId))
      } catch (error) {
        if (isMountedRef.current)
          message.error(parseErrorMsg(error) || 'Failed to search companies')
      } finally {
        if (isMountedRef.current) setIsSearching(false)
      }
    }, 300)
  }

  const handleAddCompany = (company: CompaniesModels.ICompany) => {
    setPendingAdditions((prev) => [...prev, company])
    setSearchResults([])
    setSearchQuery('')
    setSearchOpen(false)
  }

  const handleRemoveAssigned = (companyId: number) => {
    setPendingRemovals((prev) => [...prev, companyId])
  }

  const handleRemovePending = (companyId: number) => {
    setPendingAdditions((prev) => prev.filter((c) => c.id !== companyId))
  }

  const handleSave = async () => {
    try {
      await form.validateFields()
    } catch {
      return
    }

    setIsSaving(true)
    try {
      if (existingOrganization) {
        // Add companies
        for (const company of pendingAdditions) {
          await OrganizationsAPI.migrateCompany(existingOrganization.id, company.id)
        }
        // Remove companies
        for (const companyId of pendingRemovals) {
          await removeCompanyFromOrganization(existingOrganization.id, companyId)
        }
      }

      // All operations succeeded — update org name and close the drawer
      const values = form.getFieldsValue()
      onSubmit({ ...values, id: existingOrganization?.id })
    } catch (error) {
      if (isMountedRef.current) message.error(parseErrorMsg(error) || 'Failed to save organization')
    } finally {
      if (isMountedRef.current) setIsSaving(false)
    }
  }

  // For create mode, keep original simple submit behaviour
  const onFinish = (values: any) => {
    if (shouldResetFields) form.resetFields()
    onSubmit(values)
  }

  const visibleAssigned = assignedCompanies.filter((c) => !pendingRemovals.includes(c.id))
  const totalCompanyCount = visibleAssigned.length + pendingAdditions.length

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      {/* Scrollable content area — shrinks to allow footer to stay visible */}
      <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
        <Form
          form={form}
          onFinish={isEditMode ? undefined : onFinish}
          className='mb-5'
          validateMessages={validateMessages}
          name='organization'
          onValuesChange={(changedValues) => {
            if (isEditMode && 'name' in changedValues) {
              setNameChanged(changedValues.name?.trim() !== existingOrganization?.name?.trim())
            }
          }}
        >
          <Row>
            <Col span={24} className='pr-4'>
              <Form.Item
                name='name'
                label='Name'
                rules={[{ required: true }]}
                initialValue={existingOrganization?.name}
              >
                <Input prefix={<Icon type='home' style={{ color: 'rgba(0,0,0,.5)' }} />} />
              </Form.Item>
            </Col>
          </Row>

          {!isEditMode && (
            <Row>
              <Button className='ml-2' type='primary' htmlType='submit'>
                Submit
              </Button>
            </Row>
          )}
        </Form>

        {isEditMode && (
          <div className='mb-3'>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 8,
              }}
            >
              <span style={{ fontWeight: 600 }}>Companies</span>
              <span style={{ color: 'rgba(0,0,0,.45)', fontSize: 13 }}>
                {totalCompanyCount} assigned
              </span>
            </div>

            <div ref={searchContainerRef} style={{ position: 'relative' }}>
              <Input
                placeholder='Search companies to add...'
                value={searchQuery}
                prefix={<Icon type='search' />}
                suffix={isSearching ? <Icon type='loading' /> : undefined}
                onChange={(e) => {
                  const val = e.target.value
                  setSearchQuery(val)
                  setSearchOpen(true)
                  handleCompanySearch(val)
                  if (searchContainerRef.current) {
                    const r = searchContainerRef.current.getBoundingClientRect()
                    setDropdownRect({ top: r.bottom, left: r.left, width: r.width })
                  }
                }}
                onFocus={() => {
                  if (searchResults.length > 0) {
                    setSearchOpen(true)
                    if (searchContainerRef.current) {
                      const r = searchContainerRef.current.getBoundingClientRect()
                      setDropdownRect({ top: r.bottom, left: r.left, width: r.width })
                    }
                  }
                }}
                onBlur={() => setTimeout(() => setSearchOpen(false), 150)}
              />
              {searchOpen && searchResults.length > 0 && dropdownRect && (
                <div
                  style={{
                    position: 'fixed',
                    top: dropdownRect.top,
                    left: dropdownRect.left,
                    width: dropdownRect.width,
                    background: '#fff',
                    border: '1px solid #d9d9d9',
                    borderRadius: 4,
                    boxShadow: '0 2px 8px rgba(0,0,0,.15)',
                    zIndex: 1050,
                    maxHeight: 300,
                    overflowY: 'auto',
                  }}
                >
                  {searchResults.map((company) => (
                    <div
                      key={company.id}
                      onMouseDown={() => handleAddCompany(company)}
                      style={{
                        padding: '8px 12px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Icon type='plus' style={{ color: '#1890ff' }} />
                        <span>
                          {company.id}: {company.name}
                        </span>
                      </div>
                      {company.province && (
                        <span
                          style={{
                            fontSize: 12,
                            padding: '1px 6px',
                            borderRadius: 10,
                            border: '1px solid #d9d9d9',
                            color: 'rgba(0,0,0,.65)',
                            background: '#fff',
                          }}
                        >
                          {company.province}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div style={{ marginTop: 12 }}>
              {visibleAssigned.map((company) => (
                <CompanyCard
                  key={company.id}
                  name={company.name}
                  id={company.id}
                  province={company.province ?? null}
                  onRemove={() => handleRemoveAssigned(company.id)}
                />
              ))}

              {pendingAdditions.map((company) => (
                <CompanyCard
                  key={company.id}
                  name={company.name}
                  id={company.id}
                  province={company.province ?? null}
                  onRemove={() => handleRemovePending(company.id)}
                />
              ))}
            </div>
          </div>
        )}

        {isEditMode && props.children}
      </div>

      {/* Footer — pinned outside the scroll area, always visible */}
      {isEditMode && (
        <div
          style={{
            flexShrink: 0,
            background: '#fff',
            padding: '12px 0 12px 0',
            paddingRight: 72,
            borderTop: '1px solid #f0f0f0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <span style={{ color: hasPendingChanges ? '#faad14' : '#8c8c8c', fontSize: 13 }}>
            {hasPendingChanges ? 'You have unsaved changes' : 'No pending changes'}
          </span>
          <Button
            type='primary'
            onClick={handleSave}
            loading={isSaving}
            disabled={!hasPendingChanges}
          >
            Save
          </Button>
        </div>
      )}
    </div>
  )
}
